import { isDate, toNumber, split, map, join, find, isEmpty } from 'lodash-es'
import { DataItem } from 'scrivito'

import { getDataItemTitle } from './dataClassMap'
import { ensureString } from './ensureString'
import { formatDateTime } from './formatDateTime'
import { getReferencedDataItems } from './getReferencedDataItems'
import { getReferencedIds } from './getReferencedIds'
import { getLocalizedSchemaAttributes } from './schemaLocalization'
import { SchemaAttributes } from '@/Data/types'
import {
  DataItemReferenceAdd,
  DataItemReferenceGet,
} from '@/hooks/useDataItemReferenceHandler'

export const getDisplayValue = ({
  rawValue,
  key,
  schema,
  defaultFormatter = ensureString,
  get,
  add,
}: {
  rawValue: unknown
  key?: string
  schema?: DataItem
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultFormatter?: (value: any) => string
  get?: DataItemReferenceGet
  add?: DataItemReferenceAdd
}) => {
  if (isDate(rawValue)) {
    return formatDateTime(rawValue)
  }

  let attributeType

  if (key && schema) {
    attributeType = getLocalizedSchemaAttributes(
      schema?.get('attributes') as SchemaAttributes,
    )?.[key]?.type
  }

  if (attributeType === 'boolean') {
    return rawValue ? 'True' : 'False'
  }

  if (attributeType === 'date') {
    return formatDateTime(rawValue)
  }

  if (attributeType === 'number') {
    return Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(toNumber(rawValue !== undefined ? rawValue : 0))
  }

  if (attributeType === 'enumarray') {
    return join(
      map(split(rawValue as string, ','), (rawValueSlice) => {
        return getEnumValueTitle(rawValueSlice, key, schema, defaultFormatter)
      }),
      ', ',
    )
  }

  if (attributeType === 'enum') {
    return getEnumValueTitle(rawValue, key, schema, defaultFormatter)
  }

  if (attributeType === 'reference' || attributeType === 'objectarray') {
    const ids = getReferencedIds(rawValue)
    const missingIds: string[] = []

    const formattedReferencesValue = join(
      map(ids, (id) => {
        const referencedItem = get?.(id)
        if (referencedItem) {
          return defaultFormatter(getDataItemTitle(referencedItem))
        } else {
          missingIds.push(id)
          return defaultFormatter(id)
        }
      }),
      ', ',
    )

    if (!isEmpty(missingIds)) {
      if (add) {
        getReferencedDataItems({
          rawValue: missingIds,
          key: key!,
          schema: schema!,
          add: add,
        })
      }
    }

    return formattedReferencesValue
  }

  return defaultFormatter(rawValue)
}

const getEnumValueTitle = (
  rawValue: unknown,
  key?: string,
  schema?: DataItem,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultFormatter: (value: any) => string = ensureString,
) => {
  let value
  if (key && schema) {
    value = find(
      getLocalizedSchemaAttributes(
        schema.get('attributes') as SchemaAttributes,
      )?.[key]?.enumValues,
      (e) => {
        return e.value === rawValue
      },
    )?.title
  }

  if (!value) {
    value = defaultFormatter(rawValue)
  }

  return value
}
